import * as THREE from 'three/webgpu';
import { isSketchCircleObject, isSketchContour, isSketchEllipseObject } from '../../mesh';
import { isSketchSlotObject } from '../../mesh/sketch';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import type { PointSelectionCandidate } from '../../reference/reference-point/ReferencePointController';
import { SketchEntitiesTransformSelectionRole } from '../../sketch/entities-transform';
import { resolveSketchEntitiesTransformPointSnap } from '../../sketch/entities-transform/utils/SketchEntitiesTransformPointSnapResolver';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { SketchEntitySelectionHandler } from './SketchEntitySelectionHandler';

/**
 * 草图实体变换通用选择处理器。
 */
export abstract class AbstractSketchEntitiesTransformSelectionHandler extends SketchEntitySelectionHandler {
	/**
	 * @param entitySelectionManager 实体选择管理器。
	 */
	public constructor(entitySelectionManager: EntitySelectionManager) {
		super(entitySelectionManager);
	}

	/**
	 * 处理鼠标按下。
	 *
	 * @param payload 原始事件。
	 */
	public override onMouseDown(payload: RawPointerEventPayload): void {
		if (payload.button !== 0) {
			return;
		}
		if (this.resolveSelectionRole() !== SketchEntitiesTransformSelectionRole.Entities) {
			return;
		}

		//------ 草图实体变换框选 开始------
		// 实体变换命令不启用普通草图点/轮廓拖拽，但仍需要记录左键起点以支持框选。
		this.entitySelectionManager.setMouseDownStart(new THREE.Vector2(payload.clientX, payload.clientY));
		this.entitySelectionManager.setIsBoxDragging(false);
		this.entitySelectionManager.setBoxDragEnd(null);
		this.resetSelectionBoxVisual();
		this.entitySelectionManager.setSuppressNextClick(false);
		//------ 草图实体变换框选 结束------
	}

	/**
	 * 处理鼠标移动。
	 *
	 * @param payload 原始事件。
	 */
	public override onMouseMove(payload: RawPointerEventPayload): void {
		//------ 草图实体变换框选 开始------
		const mouseDownStart = this.entitySelectionManager.getMouseDownStart();
		if (
			this.resolveSelectionRole() === SketchEntitiesTransformSelectionRole.Entities
			&& mouseDownStart
			&& payload.buttons !== 0
		) {
			const dx = payload.clientX - mouseDownStart.x;
			const dy = payload.clientY - mouseDownStart.y;
			const distance = Math.sqrt(dx * dx + dy * dy);
			if (distance > this.entitySelectionManager.getBoxDragThreshold() || this.entitySelectionManager.getIsBoxDragging()) {
				this.entitySelectionManager.setIsBoxDragging(true);
				this.entitySelectionManager.setBoxDragEnd(new THREE.Vector2(payload.clientX, payload.clientY));
				this.clearHoverState();
				this.updateSelectionBoxVisual(mouseDownStart, new THREE.Vector2(payload.clientX, payload.clientY));
				return;
			}
		}

		if (payload.buttons !== 0) {
			this.clearHoverState();
			return;
		}

		if (payload.buttons === 0 && mouseDownStart) {
			this.entitySelectionManager.setMouseDownStart(null);
			this.entitySelectionManager.setIsBoxDragging(false);
			this.entitySelectionManager.setBoxDragEnd(null);
			this.resetSelectionBoxVisual();
		}
		//------ 草图实体变换框选 结束------

		//------ 草图实体变换参考点捕捉 开始------
		if (this.isPointSnapSelectionRole(this.resolveSelectionRole())) {
			this.syncReferencePointSnapPreview(payload.clientX, payload.clientY);
		}
		//------ 草图实体变换参考点捕捉 结束------

		const hoveredPick = this.pickEntityAt(
			payload.clientX,
			payload.clientY,
			this.collectHoverTargets(),
		);
		this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(hoveredPick));
	}

	/**
	 * 处理鼠标抬起。
	 *
	 * @param payload 原始事件。
	 */
	public override onMouseUp(payload: RawPointerEventPayload): void {
		if (payload.button !== 0) {
			return;
		}

		//------ 草图实体变换框选 开始------
		const dragStart = this.entitySelectionManager.getMouseDownStart();
		if (!dragStart) {
			return;
		}

		const isBoxDragging = this.entitySelectionManager.getIsBoxDragging();
		this.entitySelectionManager.setMouseDownStart(null);
		this.entitySelectionManager.setIsBoxDragging(false);
		this.entitySelectionManager.setBoxDragEnd(null);
		this.resetSelectionBoxVisual();

		if (isBoxDragging && this.resolveSelectionRole() === SketchEntitiesTransformSelectionRole.Entities) {
			this.performBoxSelection(dragStart, new THREE.Vector2(payload.clientX, payload.clientY), false);
			this.entitySelectionManager.setSuppressNextClick(true);
		}
		//------ 草图实体变换框选 结束------
	}

	/**
	 * 处理鼠标点击。
	 *
	 * @param payload 原始事件。
	 */
	public override onClick(payload: RawPointerEventPayload): void {
		if (payload.button !== 0) {
			return;
		}
		if (this.entitySelectionManager.getSuppressNextClick()) {
			this.entitySelectionManager.setSuppressNextClick(false);
			return;
		}

		if (this.resolveSelectionRole() === SketchEntitiesTransformSelectionRole.Entities) {
			const rawPicked = this.pickEntityAt(
				payload.clientX,
				payload.clientY,
				this.collectClickSelectionTargets(),
			);
			const picked = this.resolveClickSelectionPick(rawPicked);
			if (!picked) {
				const contourPick = this.entitySelectionManager.pickSketchContourAt(payload.clientX, payload.clientY);
				if (!contourPick) {
					console.debug('[移动实体选择调试] 实体选择未命中', {
						clientX: payload.clientX,
						clientY: payload.clientY,
						selectionMode: this.entitySelectionManager.getSelectionMode(),
					});
					return;
				}
				console.debug('[移动实体选择调试] 统一拾取为空，使用轮廓兜底命中', {
					contourUuid: contourPick.uuid,
					contourName: contourPick.name,
					sourceUuid: contourPick.source?.uuid,
					sourceName: contourPick.source?.name,
					sourceType: contourPick.source?.type,
					sourceConstructorName: contourPick.source?.constructor.name,
				});
				this.handleEntitySelectionPick(contourPick);
				return;
			}
			this.handleEntitySelectionPick(picked.object);
			return;
		}

		//------ 草图实体变换参考点平面交点 开始------
		/**
		 * Move / Copy 的 From 起点与 Rotate 的旋转中心统一复用 PointSnapManager；
		 * 未命中捕捉时继续使用当前草图平面投影点，保证空白区域仍可自由定义参考点。
		 */
		const candidate = this.resolveReferenceCandidateFromPointer(payload.clientX, payload.clientY);
		//------ 草图实体变换参考点平面交点 结束------
		if (!candidate) {
			return;
		}
		this.handleReferenceCandidate(candidate);
	}

	/**
	 * 解析当前选择角色。
	 *
	 * @returns 选择角色。
	 */
	protected abstract resolveSelectionRole(): SketchEntitiesTransformSelectionRole;

	/**
	 * 处理实体选择。
	 *
	 * @param object 命中对象。
	 */
	protected abstract handleEntityPicked(object: THREE.Object3D): void;

	//------ 草图实体变换框选 开始------
	/**
	 * 处理实体框选结果。
	 *
	 * @param objects 框选命中的原始对象集合。
	 */
	protected abstract handleEntitiesBoxSelected(objects: readonly THREE.Object3D[]): void;

	/**
	 * 发布框选事件并同步实体变换控制器选中集。
	 *
	 * @param selectedObjects 本次框选命中的对象集合。
	 * @param isCrossing 是否为交叉框选。
	 * @param selectionRect 本次框选的屏幕矩形。
	 *
	 * 设计说明：
	 * 1. 父类负责维持统一框选事件，保障外部监听者仍能收到框选消息；
	 * 2. 实体变换命令必须额外同步 controller 内部选中集，否则后续移动、复制、旋转没有输入实体；
	 * 3. 空框选同样下发，用于对齐普通草图框选的“替换为空选择集”语义。
	 */
	protected override publishBoxSelectionEvents(
		selectedObjects: THREE.Object3D[],
		isCrossing: boolean,
		selectionRect: { minX: number; minY: number; maxX: number; maxY: number },
	): void {
		super.publishBoxSelectionEvents(selectedObjects, isCrossing, selectionRect);
		this.handleEntitiesBoxSelected(selectedObjects);
	}
	//------ 草图实体变换框选 结束------

	//------ 草图实体变换轮廓选择 开始------
	/**
	 * 处理实体选择命中。
	 *
	 * 设计说明：
	 * 1. 普通实体变换命令的 controller 只负责消费单个可变换实体；
	 * 2. 轮廓点击属于一次命中展开多个边实体的选择语义，应收口在 selection handler；
	 * 3. 这里复用草图选择已有的轮廓关联边解析能力，保证 Move / Copy / Rotate 行为一致。
	 *
	 * @param object 当前命中对象。
	 */
	private handleEntitySelectionPick(object: THREE.Object3D): void {
		if (!isSketchContour(object)) {
			console.debug('[移动实体选择调试] 派发普通对象', {
				uuid: object.uuid,
				name: object.name,
				type: object.type,
				constructorName: object.constructor.name,
			});
			this.handleEntityPicked(object);
			return;
		}

		const contourSource = object.source;
		const slotContourOwner = this.resolveSlotContourOwner(contourSource);
		if (slotContourOwner) {
			console.debug('[移动实体选择调试] 派发槽口轮廓源对象', {
				contourUuid: object.uuid,
				contourName: object.name,
				sourceUuid: contourSource?.uuid,
				sourceName: contourSource?.name,
				sourceType: contourSource?.type,
				sourceConstructorName: contourSource?.constructor.name,
				slotUuid: slotContourOwner.uuid,
				slotName: slotContourOwner.name,
			});
			this.handleEntityPicked(slotContourOwner);
			return;
		}

		if (isSketchCircleObject(contourSource) || isSketchEllipseObject(contourSource)) {
			console.debug('[移动实体选择调试] 派发圆/椭圆轮廓源对象', {
				contourUuid: object.uuid,
				contourName: object.name,
				sourceUuid: contourSource.uuid,
				sourceName: contourSource.name,
				sourceType: contourSource.type,
				sourceConstructorName: contourSource.constructor.name,
				isCircle: isSketchCircleObject(contourSource),
				isEllipse: isSketchEllipseObject(contourSource),
			});
			this.handleEntityPicked(contourSource);
			return;
		}

		//------ 移动实体混合边轮廓选择 开始------
		const contourLinkedEntities = this.resolveContourLinkedSketchEntities(object);
		if (contourLinkedEntities.length === 0) {
			console.debug('[移动实体选择调试] 轮廓无关联边，派发轮廓对象', {
				contourUuid: object.uuid,
				contourName: object.name,
				sourceUuid: contourSource?.uuid,
				sourceName: contourSource?.name,
				sourceType: contourSource?.type,
				sourceConstructorName: contourSource?.constructor.name,
			});
			this.handleEntityPicked(object);
			return;
		}

		contourLinkedEntities.forEach((linkedEntity) => {
			console.debug('[移动实体选择调试] 派发集合轮廓关联边', {
				contourUuid: object.uuid,
				entityUuid: linkedEntity.uuid,
				entityName: linkedEntity.name,
				entityType: linkedEntity.type,
				entityConstructorName: linkedEntity.constructor.name,
			});
			this.handleEntityPicked(linkedEntity);
		});
		//------ 移动实体混合边轮廓选择 结束------
	}

	/**
	 * 解析轮廓来源是否归属于槽口整体。
	 *
	 * 设计说明：
	 * 1. 槽口轮廓命中时，source 可能是槽口本体，也可能是绑定到槽口闭合轮廓的内部边；
	 * 2. Move Entities 的选择分发必须统一回到槽口整体，否则 UI 会退化成内部直线；
	 * 3. 这里只做槽口归一，不影响圆、椭圆和普通集合轮廓的既有展开语义。
	 *
	 * @param contourSource 轮廓来源对象。
	 * @returns 槽口整体对象；未归属槽口时返回 null。
	 */
	private resolveSlotContourOwner(contourSource: THREE.Object3D | null | undefined): THREE.Object3D | null {
		if (!contourSource) {
			return null;
		}

		if (isSketchSlotObject(contourSource)) {
			return contourSource;
		}

		const closedCollection = Reflect.get(contourSource, 'closedCollection') as unknown;
		return closedCollection && isSketchSlotObject(closedCollection)
			? closedCollection
			: null;
	}
	//------ 草图实体变换轮廓选择 结束------

	/**
	 * 处理参考点候选。
	 *
	 * @param candidate 参考点候选。
	 */
	protected abstract handleReferenceCandidate(candidate: PointSelectionCandidate): void;

	/**
	 * 收集当前 hover 候选。
	 *
	 * @returns 候选对象列表。
	 */
	private collectHoverTargets(): THREE.Object3D[] {
		if (this.resolveSelectionRole() === SketchEntitiesTransformSelectionRole.Entities) {
			return this.collectClickSelectionTargets();
		}
		return this.collectReferenceTargets();
	}

	/**
	 * 收集参考点模式可拾取目标。
	 *
	 * @returns 候选对象列表。
	 */
	private collectReferenceTargets(): THREE.Object3D[] {
		return this.collectDatumReferenceInteractionTargets(this.collectClickSelectionTargets()).filter((candidate) => (
			isFeatureFace(candidate)
			|| isFeatureLine(candidate)
			|| isFeaturePoint(candidate)
			|| isDatumAxis(candidate)
			|| isDatumPoint(candidate)
			|| isDatumPlaneObject(candidate)
		));
	}

	//------ 草图实体变换参考点平面交点 开始------
	/**
	 * 从当前活动草图基准面解析自由参考点候选。
	 *
	 * 说明：
	 * 1. 草图实体变换参考点只表达用户点击位置，不表达被点中实体的端点、圆心或面中心；
	 * 2. 这里直接用当前相机射线与活动草图参考面求交，确保起点与旋转中心始终落在当前草图平面；
	 * 3. 求得交点后构造成自由点候选，后续 Move / Copy / Rotate 控制器无需区分来源。
	 *
	 * @param clientX 屏幕 X 坐标。
	 * @param clientY 屏幕 Y 坐标。
	 * @returns 自由参考点候选；无法求交时返回 null。
	 */
	private resolveReferenceCandidateFromSketchPlane(
		clientX: number,
		clientY: number,
	): PointSelectionCandidate | null {
		const container = this.entitySelectionManager.renderer.container;
		const camera = this.entitySelectionManager.renderer.camera;
		const referencePlane = this.entitySelectionManager.renderer.sketchGroupManager?.activeSketchGroup?.referencePlane;
		if (!container || !camera || !referencePlane) {
			return null;
		}

		const rect = container.getBoundingClientRect();
		this.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
		this.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
		this.raycaster.setFromCamera(this.mouseNdc, camera);

		const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
			referencePlane.normal.clone(),
			referencePlane.coplanarPoint(new THREE.Vector3()),
		);
		const intersection = new THREE.Vector3();
		const worldPoint = this.raycaster.ray.intersectPlane(plane, intersection);
		if (!worldPoint) {
			return null;
		}

		return this.createFreePointSelectionCandidate(
			worldPoint.clone(),
			`sketch-plane-free-point-${worldPoint.x}-${worldPoint.y}-${worldPoint.z}`,
			'所定义的点',
		);
	}

	//------ 草图实体变换参考点捕捉 开始------
	/**
	 * 从屏幕坐标解析参考点候选。
	 *
	 * @param clientX 屏幕 X 坐标。
	 * @param clientY 屏幕 Y 坐标。
	 * @returns 参考点候选。
	 *
	 * 设计说明：
	 * 1. Move / Copy From 起点和 Rotate 旋转中心应与草图绘制使用相同的点捕捉规则；
	 * 2. 捕捉求解交给 PointSnapManager，选择处理器只负责把捕捉结果转换成参考点候选；
	 * 3. ToPoint 由移动/复制控制器的指针预览链路负责，其他角色保留自由平面点逻辑。
	 */
	private resolveReferenceCandidateFromPointer(clientX: number, clientY: number): PointSelectionCandidate | null {
		const freePointCandidate = this.resolveReferenceCandidateFromSketchPlane(clientX, clientY);
		if (!freePointCandidate || !this.isPointSnapSelectionRole(this.resolveSelectionRole())) {
			return freePointCandidate;
		}

		const freeWorldPoint = this.resolvePointCandidateWorldPoint(freePointCandidate);
		if (!freeWorldPoint) {
			return freePointCandidate;
		}

		const snapResolution = resolveSketchEntitiesTransformPointSnap({
			viewer: this.entitySelectionManager.renderer,
			clientX,
			clientY,
			worldPoint: freeWorldPoint,
		});
		if (!snapResolution) {
			return freePointCandidate;
		}

		return this.createFreePointSelectionCandidate(
			snapResolution.position,
			this.createPointSnapCandidateId(snapResolution.position),
			snapResolution.label,
		);
	}

	/**
	 * 同步实体变换参考点选择阶段的点捕捉预览。
	 *
	 * @param clientX 屏幕 X 坐标。
	 * @param clientY 屏幕 Y 坐标。
	 */
	private syncReferencePointSnapPreview(clientX: number, clientY: number): void {
		const freePointCandidate = this.resolveReferenceCandidateFromSketchPlane(clientX, clientY);
		const freeWorldPoint = freePointCandidate ? this.resolvePointCandidateWorldPoint(freePointCandidate) : null;
		if (!freeWorldPoint) {
			return;
		}

		// mousemove 只刷新捕捉指示器和点高亮，真正写入参考点仍由 click 完成。
		resolveSketchEntitiesTransformPointSnap({
			viewer: this.entitySelectionManager.renderer,
			clientX,
			clientY,
			worldPoint: freeWorldPoint,
		});
	}

	/**
	 * 判断当前选择角色是否应使用公共点捕捉。
	 *
	 * @param role 当前实体变换选择角色。
	 * @returns From 起点或旋转中心选择阶段返回 true。
	 *
	 * 设计说明：
	 * 1. From 起点和旋转中心都属于一次性独立点输入，应共享全局捕捉配置与视觉反馈；
	 * 2. To 目标点已有移动/复制控制器驱动的连续预览入口，不能在这里重复求解；
	 * 3. 实体和镜向轴选择不是点输入，必须继续走各自拾取链路。
	 */
	private isPointSnapSelectionRole(role: SketchEntitiesTransformSelectionRole): boolean {
		return role === SketchEntitiesTransformSelectionRole.FromPoint
			|| role === SketchEntitiesTransformSelectionRole.RotationCenter;
	}

	/**
	 * 读取点候选中的世界坐标。
	 *
	 * @param candidate 参考点候选。
	 * @returns 世界坐标；候选不携带点坐标时返回 null。
	 */
	private resolvePointCandidateWorldPoint(candidate: PointSelectionCandidate): THREE.Vector3 | null {
		return candidate.payload?.point?.clone() ?? null;
	}

	/**
	 * 创建点捕捉候选唯一标识。
	 *
	 * @param worldPoint 捕捉后的世界坐标。
	 * @returns 候选 ID。
	 */
	private createPointSnapCandidateId(worldPoint: THREE.Vector3): string {
		return `sketch-point-snap-${worldPoint.x}-${worldPoint.y}-${worldPoint.z}`;
	}
	//------ 草图实体变换参考点捕捉 结束------
	//------ 草图实体变换参考点平面交点 结束------

	/**
	 * 构造自由点候选。
	 *
	 * @param worldPoint 世界坐标。
	 * @param entityId 候选唯一标识。
	 * @param label 展示名称。
	 * @returns 自由点候选。
	 */
	private createFreePointSelectionCandidate(
		worldPoint: THREE.Vector3,
		entityId: string,
		label: string,
	): PointSelectionCandidate {
		return {
			entityId,
			entityType: 'point' as PointSelectionCandidate['entityType'],
			label,
			payload: {
				point: worldPoint,
			} as PointSelectionCandidate['payload'],
		};
	}
}
